import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { css } from 'emotion';
import { colors, TextField, Button, Icon, Select } from '@cimpress/react-components';
import { Spinner } from '@cimpress/react-components/lib/shapes';
import * as jsSearch from 'js-search';

import { actions as configActions, selectors as configSelectors } from '../../../reducers/configurations';
import { selectors as identitySelectors } from '../../../reducers/identities';
import { getCounterType } from '../../../reducers/identities/utils';
import ApprovalToggle from './ApprovalToggle';

const toggleContainerStyle = css({
  borderBottom: `2px solid ${colors.alloy}`
});

const identityContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  marginBottom: '60px'
});

const filterStyle = css({
  width: '100%',
  marginTop: '20px'
});

const buttonStyle = css({
  marginBottom: '20px',
  '&[disabled]:focus': {
    backgroundColor: `${colors.alloy} !important`
  }
});

const labelStyle = css({
  width: '185px',
  marginRight: '20px',
  textAlign: 'right'
});

class ApprovalSettings extends PureComponent {
  state = {
    selections: [],
    showApprovalModal: false,
    searchText: ''
  };

  componentDidMount() {
    // if selected type has only one identity, pre-select and hide the select field
    // otherwise, pre-select the first identity on the list and display the select field
    const { onSelectIdentity, authorizedIdentities } = this.props;
    onSelectIdentity(authorizedIdentities[0]);
  }

  componentDidUpdate(prevProps, prevState) {
    const { identityId, identityType, onSelectIdentity, authorizedIdentities, fetchAutoApproval } = this.props;
    // for auto approval settings, if the identity type changes clear the
    // previous identity selection and pre-fill
    if (prevProps.identityType !== identityType) {
      onSelectIdentity(authorizedIdentities[0]);
      this.setState({ selections: [] });
    }

    if (prevProps.identityId !== identityId) {
      fetchAutoApproval(identityId, identityType);
    }
  }

  setApprovalConfigurations = approvals => {
    const { identityId, identityType, updateAutoApproval } = this.props;
    const type = getCounterType(identityType);
    const autoApprovalSettings = approvals.map(({ autoApprove, id }) => ({ otherEntity: { id, type }, autoApprove }));
    updateAutoApproval(identityId, identityType, { autoApprovalSettings });
  };

  onChangeFilter = e => {
    const searchText = e.target.value;
    this.setState({ searchText });
  };

  makeSearchParams = sortedData => {
    const search = new jsSearch.Search('id');
    search.addIndex('id');
    search.addIndex('name');
    search.addIndex('autoApprove');
    search.addDocuments(sortedData);

    return search;
  };

  filterConfigurations = data => {
    const { searchText } = this.state;
    const jsSearch = this.makeSearchParams(data);
    return !searchText || searchText === '' ? data : jsSearch.search(searchText);
  };

  onToggle = counterPartyId => {
    const currentConfigs = this.props.currentConfigurations;
    const updatedApprovals = currentConfigs.map(approval => {
      const { id, autoApprove } = approval;
      return id === counterPartyId ? { ...approval, autoApprove: !autoApprove } : approval;
    });

    this.setApprovalConfigurations(updatedApprovals);
  };

  onAddApprovals = () => {
    const approvals = this.state.selections;

    const currentConfigs = this.props.currentConfigurations;
    const newApprovals = approvals.map(({ value }) => ({ id: value, autoApprove: true }));
    const filteredCurrentConfigs = currentConfigs.filter(({ id, autoApprove }) => autoApprove);

    const updatedApprovals = [...filteredCurrentConfigs, ...newApprovals];
    this.setApprovalConfigurations(updatedApprovals);

    this.setState({ selections: [] });
  };

  onApproverSelect = selections => {
    this.setState({ selections });
  };

  render() {
    const { searchText, selections } = this.state;
    const {
      t,
      isFetchingConfigurations,
      currentConfigurations,
      identityId,
      identityType,
      authorizedIdentities,
      selectionOptions,
      onSelectIdentity
    } = this.props;

    const header = identityId &&
      identityType && (
        <div>
          <Button type="primary" className={buttonStyle} onClick={this.onAddApprovals} disabled={!selections.length}>
            {t('settings:autoApprovals.addButton')}
          </Button>
          {currentConfigurations.length > 0 && (
            <div className="settings__search-header">
              <TextField
                className={filterStyle}
                value={searchText}
                onChange={this.onChangeFilter}
                label={t('settings:autoApprovals.filterInputLabel')}
              />
              <span className="settings__auto-approval-icon">
                <Icon name="search" size="lg" color={colors.ocean.base} />
              </span>
            </div>
          )}
        </div>
      );

    const approvals =
      currentConfigurations && currentConfigurations.length ? (
        <div className={toggleContainerStyle}>
          {this.filterConfigurations(currentConfigurations).map(party => (
            <ApprovalToggle
              key={party.id}
              partyId={party.id}
              partyType={getCounterType(identityType)}
              partyName={party.name}
              autoApprove={party.autoApprove}
              onToggle={this.onToggle}
            />
          ))}
        </div>
      ) : null;

    const approverOption = (
      <Select
        style={{ width: '350px' }}
        label={t(`settings:autoApprovals.select_${identityType}`)}
        value={identityId}
        options={authorizedIdentities.map(({ id, name }) => ({ label: name, value: id }))}
        onChange={onSelectIdentity}
      />
    );

    const initiatorOption = (
      <Select
        style={{ width: '350px' }}
        label={t(`settings:autoApprovals.select_${getCounterType(identityType)}`, { count: 2 })}
        value={selections}
        options={selectionOptions}
        onChange={this.onApproverSelect}
        multi
      />
    );

    const identitySelector = (
      <div className={identityContainerStyle}>
        <h4>{t('settings:autoApprovals.header2')}</h4>
        <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0px' }}>
          <h5 className={labelStyle}>{t('settings:autoApprovals.approvalNeeded')}</h5>
          {approverOption}
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h5 className={labelStyle}>{t('settings:autoApprovals.createdByLabel')}</h5>
          {initiatorOption}
        </div>
      </div>
    );

    return isFetchingConfigurations ? (
      <div className={'loading-container'}>
        <Spinner />
        <h5>{t('settings:autoApprovals.fetchingMessage')}</h5>
      </div>
    ) : (
      <div>
        {identitySelector}
        {header}
        {approvals}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { identityId, identityType } = ownProps;
  return {
    authorizedIdentities: identitySelectors.getAuthorizedIdentities(state, identityType),
    isFetchingConfigurations: configSelectors.getisFetchingConfigurations(state),
    currentConfigurations: configSelectors.getCurrentConfigurations(state, identityId, identityType),
    selectionOptions: configSelectors.getSelectionOptions(state, identityId, identityType)
  };
};

const mapDispatchToProps = {
  fetchAutoApproval: configActions.fetchAutoApproval,
  updateAutoApproval: configActions.updateAutoApproval
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ApprovalSettings));
